import { useCallback, useMemo, useRef, useState } from "react";

const HISTORY_LIMIT = 50;

let seq = 0;
const nextId = () => `item-${Date.now()}-${(seq += 1)}`;

/**
 * Everything on the board, and the operations that change it.
 *
 * Items are one flat list of `{ id, kind, ... }` rather than a list per kind:
 * z-order is then just array order, and select / move / delete are written
 * once instead of four times.
 *
 * Kept separate from the components so the board can be driven by a test, or
 * later persisted, without touching the rendering.
 */
/**
 * The operations that turn the board from `now` back into `before`.
 *
 * Re-adding an item with its id replaces it wherever it is, so a change
 * undone and a deletion undone are the same operation: put it back as it
 * was. Order on the board (what is on top) is not replayed; it is the one
 * thing an undo leaves as it is on other screens.
 */
/** The same things on the board — the same item and stroke objects —
 *  whatever order they are in. Order is only what is on top. */
const sameBoard = (a, b) => {
  if (a.items.length !== b.items.length || a.strokes.length !== b.strokes.length) return false;
  const items = new Set(b.items);
  const strokes = new Set(b.strokes);
  return a.items.every((it) => items.has(it)) && a.strokes.every((st) => strokes.has(st));
};

const undoOps = (now, before) => {
  const ops = [];
  const beforeItems = new Map(before.items.map((it) => [it.id, it]));
  const nowItems = new Map(now.items.map((it) => [it.id, it]));
  now.items.forEach((it) => {
    if (!beforeItems.has(it.id)) ops.push({ type: "item.removed", id: it.id });
  });
  before.items.forEach((it) => {
    if (nowItems.get(it.id) !== it) ops.push({ type: "item.added", item: it });
  });
  const beforeStrokes = new Map(before.strokes.map((st) => [st.id, st]));
  const nowStrokes = new Map(now.strokes.map((st) => [st.id, st]));
  now.strokes.forEach((st) => {
    if (!beforeStrokes.has(st.id)) ops.push({ type: "stroke.removed", id: st.id });
  });
  before.strokes.forEach((st) => {
    const current = nowStrokes.get(st.id);
    if (!current) ops.push({ type: "stroke.added", stroke: st });
    else if (current !== st) ops.push({ type: "stroke.updated", id: st.id, patch: { points: st.points } });
  });
  return ops;
};

export function useBoard({ onOp } = {}) {
  // Every local change announces itself, so the room can send it on. Held in
  // a ref rather than a dependency so the operations below keep stable
  // identities and do not re-create the whole board on every render.
  const emit = useRef(onOp);
  emit.current = onOp;

  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [strokes, setStrokes] = useState([]);

  // What is on the board right now, for the operations that need to look
  // before they change it without taking `items` as a dependency.
  const latest = useRef(items);
  latest.current = items;
  const strokesLatest = useRef(strokes);
  strokesLatest.current = strokes;

  /**
   * What the board looked like before each of the last few changes.
   *
   * Snapshots rather than inverse operations: the lists are small, and a
   * snapshot cannot drift out of step with the thing it undoes. Bounded,
   * because an hour of teaching should not grow without limit.
   */
  const past = useRef([]);
  const remember = useCallback(() => {
    const last = past.current[past.current.length - 1];
    // One gesture often makes two changes — adding a note and the line to it
    // happen in the same click, and React has not re-rendered in between, so
    // both would record the same board. Keeping the duplicate would cost a
    // second press of undo to step back one action.
    if (last && last.items === latest.current && last.strokes === strokesLatest.current) return;
    past.current = [
      ...past.current.slice(-(HISTORY_LIMIT - 1)),
      { items: latest.current, strokes: strokesLatest.current },
    ];
  }, []);

  /**
   * Step back one change — on every screen, not only this one.
   *
   * The board goes back to its snapshot here, and the room is sent what
   * that took: whatever had been added is removed, whatever had been
   * removed or changed is put back as it was. Without this, undo only
   * happened on the teacher's screen and every student kept the note.
   */
  const undo = useCallback(() => {
    // Picking something up marks the board before a drag that may never
    // come (and raises it, which reorders the list but changes nothing);
    // a mark with nothing after it is not a step to undo, so skip past
    // any that hold the same things as the board does now.
    let previous = past.current.pop();
    const now = { items: latest.current, strokes: strokesLatest.current };
    while (previous && sameBoard(previous, now)) previous = past.current.pop();
    if (!previous) return false;
    const ops = undoOps(
      { items: latest.current, strokes: strokesLatest.current },
      previous
    );
    setItems(previous.items);
    setStrokes(previous.strokes);
    setSelectedId(null);
    ops.forEach((op) => emit.current?.(op));
    return true;
  }, []);

  const canUndo = () => past.current.length > 0;

  const add = useCallback((item) => {
    remember();
    const created = { id: nextId(), ...item };
    setItems((prev) => [...prev, created]);
    emit.current?.({ type: "item.added", item: created });
    return created;
  }, [remember]);

  /**
   * Change an item.
   *
   * `record: false` is for the frames of a gesture — a drag or a resize fires
   * this on every pointer move, and recording each one made undo step back a
   * few pixels at a time. The gesture records once, when it starts, through
   * `checkpoint`, so undo takes back the whole move the way it does in any
   * whiteboard.
   */
  const update = useCallback((id, patch, { record = true } = {}) => {
    if (record) remember();
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    emit.current?.({ type: "item.updated", id, patch });
  }, [remember]);

  /** Mark the board as it is now, before a gesture changes it. */
  const checkpoint = useCallback(() => remember(), [remember]);

  /**
   * Remove an item, and any connector attached to it.
   *
   * A connector that outlived one of its ends would have nothing to draw
   * between, so it goes at the same time. Each removal is its own operation
   * rather than one compound one, so the far side of the stream applies them
   * with the same applier it already has.
   */
  const remove = useCallback((id) => {
    remember();
    // Ink is selectable too, and goes the same way.
    if (strokesLatest.current.some((s) => s.id === id)) {
      setStrokes((prev) => prev.filter((s) => s.id !== id));
      setSelectedId((prev) => (prev === id ? null : prev));
      emit.current?.({ type: "stroke.removed", id });
      return;
    }
    // Read from the ref, not from inside the updater: an updater can run
    // twice under StrictMode, and announcing a removal twice would send the
    // room two operations for one click.
    const orphaned = latest.current.filter(
      (it) => it.kind === "connector" && (it.from === id || it.to === id)
    );
    const gone = new Set([id, ...orphaned.map((o) => o.id)]);
    setItems((prev) => prev.filter((it) => !gone.has(it.id)));
    setSelectedId((prev) => (prev === id ? null : prev));
    orphaned.forEach((connector) =>
      emit.current?.({ type: "item.removed", id: connector.id })
    );
    emit.current?.({ type: "item.removed", id });
  }, [remember]);

  /** Join two items with a line. Connectors are items, so they persist, sync
   *  and undo through exactly the same path as everything else. */
  const connect = useCallback(
    (fromId, toId, colour) => add({ kind: "connector", from: fromId, to: toId, colour }),
    [add]
  );

  /** Moving an item to the end of the list is what puts it on top. */
  const bringToFront = useCallback((id) => {
    setItems((prev) => {
      const item = prev.find((it) => it.id === id);
      if (!item) return prev;
      return [...prev.filter((it) => it.id !== id), item];
    });
    emit.current?.({ type: "item.raised", id });
  }, []);

  const addStroke = useCallback((stroke) => {
    remember();
    setStrokes((prev) => [...prev, stroke]);
    emit.current?.({ type: "stroke.added", stroke });
  }, [remember]);

  /**
   * Change a stroke — moving one rewrites its points.
   *
   * Only ever called once per gesture, when the stroke is put down: the drag
   * itself is shown with a transform, so a line of three hundred points is
   * sent once rather than on every frame of the move.
   */
  const updateStroke = useCallback((id, patch) => {
    remember();
    setStrokes((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    emit.current?.({ type: "stroke.updated", id, patch });
  }, [remember]);

  /** Load a whole board at once — what the server sends back. */
  const replaceAll = useCallback((nextItems = [], nextStrokes = []) => {
    setItems(Array.isArray(nextItems) ? nextItems : []);
    setStrokes(Array.isArray(nextStrokes) ? nextStrokes : []);
    setSelectedId(null);
  }, []);

  const clear = useCallback(() => {
    remember();
    setItems([]);
    setStrokes([]);
    setSelectedId(null);
    emit.current?.({ type: "board.cleared" });
  }, [remember]);

  /**
   * An operation from the stream. Applied silently: re-emitting it would
   * send someone else's change back to them as if it were ours.
   */
  const applyOp = useCallback((op) => {
    if (!op || !op.type) return;
    switch (op.type) {
      case "item.added":
        setItems((prev) =>
          prev.some((i) => i.id === op.item.id)
            ? prev.map((i) => (i.id === op.item.id ? op.item : i))
            : [...prev, op.item]
        );
        break;
      case "item.updated":
        setItems((prev) => prev.map((i) => (i.id === op.id ? { ...i, ...op.patch } : i)));
        break;
      case "item.removed":
        setItems((prev) => prev.filter((i) => i.id !== op.id));
        break;
      case "item.raised":
        setItems((prev) => {
          const item = prev.find((i) => i.id === op.id);
          return item ? [...prev.filter((i) => i.id !== op.id), item] : prev;
        });
        break;
      case "stroke.added":
        setStrokes((prev) =>
          prev.some((s) => s.id === op.stroke.id) ? prev : [...prev, op.stroke]
        );
        break;
      case "stroke.updated":
        setStrokes((prev) => prev.map((s) => (s.id === op.id ? { ...s, ...op.patch } : s)));
        break;
      case "stroke.removed":
        setStrokes((prev) => prev.filter((s) => s.id !== op.id));
        break;
      case "board.cleared":
        setItems([]);
        setStrokes([]);
        setSelectedId(null);
        break;
      default:
        break;
    }
  }, []);

  const isEmpty = items.length === 0 && strokes.length === 0;

  return useMemo(
    () => ({
      items, strokes, selectedId, isEmpty,
      add, update, remove, connect, bringToFront, addStroke, updateStroke, replaceAll, applyOp, clear, setSelectedId,
      undo, canUndo, checkpoint,
    }),
    [items, strokes, selectedId, isEmpty, add, update, remove, connect, bringToFront, addStroke, updateStroke, replaceAll, applyOp, clear, undo, checkpoint]
  );
}
